import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { ChevronDown, Lock, Plus } from "lucide-react";
import { rolesPermissionsService } from "../../services/api";
import { toast } from "react-hot-toast";
import { usePermission } from "../../hooks/usePermission";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { buildRolePermissionCodenames } from "./rolePermissionsMerge";
import Tooltip from "../../components/common/Tooltip";
import useUiLanguage from "../../components/profile/coaching-personality/useUiLanguage";
import i18n from "../../i18n";
import { getStaticTranslation } from "../../translations/registerStaticTranslations";
import ConfirmationModal from "../../components/club/ConfirmationModal";

// Read-only rows (CEO, Default Staff Role) arrive from GET /users/roles/ with
// is_editable:false, and users/role_views.assign_permissions answers 400 for
// them. Until now the only sign of that was a pale checkbox, which says that
// the row is dead but never why — so the role cell carries a lock and this
// line.
export const READ_ONLY_ROLE_HINT = "Set by the platform and cannot be edited.";

// The catalogue, grouped into the six sections Marin fixed (V3 gate, "HUB-UI ·
// GRUPPIERUNG DER RECHTE-ZEILEN"). One section per tab; every codename below
// is enforced server-side, so no switch in this hub is a placebo any more.
//
// Gone from the UI with this change (V3 gate B9, "vier bauen, vier
// streichen"): accept_tenders (duplicate of accept_pitches),
// contact_scouts_other_clubs, contact_head_scout, internal_contact_everyone
// (internal contact is canonically free), and delete_user — replaced by the
// real remove_staff right, the old codename is struck and NOT inherited. The
// three share_pitches_* codenames collapse into one share_pitches.
// Striking is additive-safe: the codename simply stops being rendered, and
// rolePermissionsMerge carries whatever a role still holds through the save.
//
// Back in the UI: edit_roles (B8) — the meta-right was dropped in 2025
// without a reason and must be grantable and revocable again.
//
// Labels follow the house convention documented in the toggle audit: inside a
// thematic section the header carries the verb alone; a standalone right reads
// CAN <VERB> <OBJECT>; a relationship right drops the "CAN"; "\n" is a
// deliberate line break. Every label here has a row in
// src/translations/pages/club/roles-and-responsibilities.json — a label
// without one stays English in all 16 languages.
//
// Exported so the catalogue can be pinned by a test instead of being inferred
// from rendered checkboxes.
export const PERMISSION_CATEGORIES = {
  requests: [
    { key: "view_tenders", label: "VIEW" },
    { key: "create_tenders", label: "CREATE" },
    { key: "edit_tenders", label: "EDIT" },
    { key: "delete_tenders", label: "DELETE" },
  ],
  // share_pitches was here and is gone again (Marin, 2026-08-05): the backend
  // has no pitch-share surface at all — the chat share action supports seven
  // object types and `pitch` is not one of them, there is no share model, no
  // attachment and no FE action. Shipping the row would have created exactly
  // the dead switch this phase exists to abolish. The codename leaves the
  // backend catalogue in a paired follow-up PR.
  pitches: [
    { key: "view_pitches", label: "VIEW" },
    { key: "accept_pitches", label: "ACCEPT" },
  ],
  // sign_contracts was here and is gone again (Marin, 2026-08-05): both
  // candidate enforcement surfaces are used by the AGENCY side too, and a
  // contract is signed by both parties — a club-only right would have locked
  // agents out of signing. Rather than invent a conditional gate, the right
  // goes; signing stays governed by who is actually a party to the document.
  // manage_document_templates keeps its gate since BE #300.
  contracts: [
    {
      key: "manage_document_templates",
      label: "CAN MANAGE DOCUMENT\nTEMPLATES",
    },
  ],
  squad_scouting: [
    { key: "edit_squad", label: "EDIT SQUAD" },
    { key: "access_radar", label: "CAN ACCESS RADAR" },
  ],
  communication: [
    { key: "contact_club", label: "CONTACT OTHER CLUBS" },
    { key: "contact_agents", label: "CONTACT AGENTS" },
    { key: "talk_to_organizer", label: "CAN TALK TO ORGANIZER" },
  ],
  team_permissions: [
    { key: "invite_new_staff", label: "CAN INVITE NEW STAFF" },
    { key: "remove_staff", label: "CAN REMOVE STAFF" },
    { key: "edit_roles", label: "CAN EDIT ROLES" },
  ],
};

// The six section headings, in Marin's order and wording. House rule: they
// stay ENGLISH in all 16 languages. That is why each one DOES carry a row in
// src/translations/pages/club/roles-and-responsibilities.json whose every
// language column repeats the English string — a static hit short-circuits the
// translation service, whereas an unregistered string would be machine
// translated in 15 languages and stay English only in Croatian
// (STATIC_ONLY_LANGS, contexts/TranslationContext.js:26).
export const SECTIONS = [
  { id: "requests", label: "Requests" },
  { id: "pitches", label: "Pitches" },
  { id: "contracts", label: "Contracts & documents" },
  { id: "squad_scouting", label: "Squad & scouting" },
  { id: "communication", label: "Communication" },
  { id: "team_permissions", label: "Team & permissions" },
];

const RolesAndResponsibilities = () => {
  // The DOM scanner (TranslationContext) translates rendered text by exact
  // English-source match; it never sees the button label below because the
  // count/plural is baked into the string before it reaches JSX (see the
  // save-button render, QAV5-004 Befund B). Same reuse-first pattern as
  // MegaMenu.js:119 ("Show more (+{count})"): fetch the raw template via the
  // exact-key lookup and substitute the placeholders ourselves, instead of
  // routing through the fuzzy template matcher (whose `(.+?)` capture groups
  // cannot match the empty-string singular suffix).
  //
  // `useUiLanguage()` instead of the throwing `useTranslation()` hook
  // (contexts/TranslationContext.js): this page's own test suite
  // (RolesAndResponsibilities.save.test.js) renders `<RolesAndResponsibilities
  // />` directly with no `<TranslationProvider>` above it, exactly the case
  // useUiLanguage's own header comment documents. Caught by actually running
  // that suite after this fix — useTranslation() threw
  // "must be used within a TranslationProvider" in all 13 of its render-based
  // tests. useUiLanguage reads the same context without the provider
  // requirement and falls back to English, which is correct here regardless.
  const currentLanguage = useUiLanguage();
  const tr = (s) => getStaticTranslation(i18n, s, currentLanguage) || s;
  const [activeTab, setActiveTab] = useState("requests");
  const [permissions, setPermissions] = useState({});
  const [originalPermissions, setOriginalPermissions] = useState({}); // Track original state
  const [roles, setRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Create-role modal (QAV5 custom-club-role). The button that opens it is
  // gated by canEditRoles below — same state the read-only checkboxes use,
  // no new permission check introduced.
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [creatingRole, setCreatingRole] = useState(false);

  // Permission checks
  const canEditRoles = usePermission("edit_roles");

  // Below the tablet breakpoint the grid renders as a role accordion instead
  // of the table (LOOPUI Fix 2). 767px matches the newer surfaces
  // (InboxTabs, ClubRadar): at exactly 768px the table still fits.
  // Conditional render (not CSS hiding) so the checkboxes exist only once in
  // the DOM — this page's render-based tests count them.
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [expandedRoleIds, setExpandedRoleIds] = useState(() => new Set());
  const toggleRoleExpanded = (roleId) => {
    setExpandedRoleIds((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) {
        next.delete(roleId);
      } else {
        next.add(roleId);
      }
      return next;
    });
  };

  // Scroll hint for the tab bar: the fade only shows while more tabs are
  // hidden to the right, so the bar never cuts off silently.
  const tabsNavRef = useRef(null);
  const [tabsHaveMoreRight, setTabsHaveMoreRight] = useState(false);
  const updateTabsHint = useCallback(() => {
    const el = tabsNavRef.current;
    if (!el) return;
    setTabsHaveMoreRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
  }, []);
  useEffect(() => {
    updateTabsHint();
    window.addEventListener("resize", updateTabsHint);
    return () => window.removeEventListener("resize", updateTabsHint);
  }, [updateTabsHint, loading]);

  const permissionCategories = PERMISSION_CATEGORIES;

  // Load roles and permissions from API. Extracted out of the mount effect
  // (unchanged logic) so the create-role flow can call the exact same
  // GET+rebuild pipeline to bring a freshly created role into every tab's
  // permission map, instead of hand-splicing one more role into six nested
  // state objects.
  const loadRolesAndPermissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await rolesPermissionsService.getRolesAndPermissions();

      if (result.success) {
        const { roles: apiRoles, permissions: apiPermissions } = result.data;

        setRoles(apiRoles || []);
        setAvailablePermissions(apiPermissions || []);

        // Build permissions state from API data
        const permissionsState = {};

        // Initialize permissions for each tab
        Object.keys(permissionCategories).forEach((tabId) => {
          permissionsState[tabId] = {};

          apiRoles.forEach((role) => {
            permissionsState[tabId][role.id] = {};

            // Check each permission for this role
            permissionCategories[tabId].forEach((permission) => {
              const hasPermission =
                role.permission_codenames?.includes(permission.key) || false;
              permissionsState[tabId][role.id][permission.key] =
                hasPermission;
            });
          });
        });

        setPermissions(permissionsState);
        setOriginalPermissions(JSON.parse(JSON.stringify(permissionsState))); // Deep copy for tracking changes
      } else {
        setError(result.error);
        toast.error(result.error);
        // No roles were loaded, so the grid has no rows to render — the old
        // `defaultPermissions` fallback object could never surface (it was
        // also keyed by role slug and short verb, never by role id and
        // codename, so no lookup in this file ever hit it). Removed with
        // this change; the error line above is the whole failure state.
        setPermissions({});
        setOriginalPermissions({});
      }
    } catch (err) {
      console.error("Error loading roles and permissions:", err);
      setError("Failed to load roles and permissions");
      toast.error("Failed to load roles and permissions");
      setPermissions({});
      setOriginalPermissions({});
    } finally {
      setLoading(false);
    }
  }, [permissionCategories]);

  useEffect(() => {
    loadRolesAndPermissions();
  }, [loadRolesAndPermissions]);

  // Custom SVG icons for each section
  const customIcons = {
    requests: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M6.66667 5H17.5M6.66667 10H17.5M6.66667 15H17.5M2.5 5H2.50833M2.5 10H2.50833M2.5 15H2.50833"
          stroke="#274AB1"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    pitches: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M6.66667 5H17.5M6.66667 10H17.5M6.66667 15H17.5M2.5 5H2.50833M2.5 10H2.50833M2.5 15H2.50833"
          stroke="#274AB1"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    // Contracts & documents — a sheet with a folded corner, drawn in the same
    // 20x18 box, stroke and cap style as its neighbours in this map.
    contracts: (
      <svg
        width="20"
        height="18"
        viewBox="0 0 20 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11.6666 1.5H5.83325C5.3912 1.5 4.96730 1.67559 4.65474 1.98816C4.34218 2.30072 4.16659 2.72464 4.16659 3.16667V16.5C4.16659 16.942 4.34218 17.366 4.65474 17.6785C4.9673 17.9911 5.3912 18.1667 5.83325 18.1667H15.8333C16.2753 18.1667 16.6992 17.9911 17.0118 17.6785C17.3243 17.366 17.4999 16.942 17.4999 16.5V7.33333L11.6666 1.5Z"
          stroke="#274AB1"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M11.6666 1.5V7.33333H17.4999M7.49992 11.5H14.1666M7.49992 14.8333H14.1666"
          stroke="#274AB1"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    squad_scouting: (
      <svg
        width="20"
        height="18"
        viewBox="0 0 20 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M14.1666 16.5V14.8333C14.1666 13.9493 13.8154 13.1014 13.1903 12.4763C12.5652 11.8512 11.7173 11.5 10.8333 11.5H4.16659C3.28253 11.5 2.43468 11.8512 1.80956 12.4763C1.18444 13.1014 0.833252 13.9493 0.833252 14.8333V16.5M19.1666 16.5V14.8333C19.166 14.0948 18.9202 13.3773 18.4677 12.7936C18.0152 12.2099 17.3817 11.793 16.6666 11.6083M13.3333 1.60833C14.0503 1.79192 14.6858 2.20892 15.1396 2.79359C15.5935 3.37827 15.8398 4.09736 15.8398 4.8375C15.8398 5.57764 15.5935 6.29673 15.1396 6.88141C14.6858 7.46608 14.0503 7.88308 13.3333 8.06667M10.8333 4.83333C10.8333 6.67428 9.34087 8.16667 7.49992 8.16667C5.65897 8.16667 4.16659 6.67428 4.16659 4.83333C4.16659 2.99238 5.65897 1.5 7.49992 1.5C9.34087 1.5 10.8333 2.99238 10.8333 4.83333Z"
          stroke="#274AB1"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    communication: (
      <svg
        width="20"
        height="16"
        viewBox="0 0 20 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M18.3333 8.00004H13.3333L11.6666 10.5H8.33329L6.66663 8.00004H1.66663M18.3333 8.00004V13C18.3333 13.4421 18.1577 13.866 17.8451 14.1786C17.5326 14.4911 17.1087 14.6667 16.6666 14.6667H3.33329C2.89127 14.6667 2.46734 14.4911 2.15478 14.1786C1.84222 13.866 1.66663 13.4421 1.66663 13V8.00004M18.3333 8.00004L15.4583 2.25837C15.3203 1.9807 15.1076 1.74702 14.8441 1.58361C14.5806 1.4202 14.2767 1.33354 13.9666 1.33337H6.03329C5.72322 1.33354 5.41935 1.4202 5.15583 1.58361C4.89231 1.74702 4.67961 1.9807 4.54163 2.25837L1.66663 8.00004"
          stroke="#274AB1"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    team_permissions: (
      <svg
        width="20"
        height="18"
        viewBox="0 0 20 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M14.1667 16.5V14.8333C14.1667 13.9493 13.8155 13.1014 13.1904 12.4763C12.5653 11.8512 11.7174 11.5 10.8334 11.5H4.16671C3.28265 11.5 2.43481 11.8512 1.80968 12.4763C1.18456 13.1014 0.833374 13.9493 0.833374 14.8333V16.5M19.1667 16.5V14.8333C19.1662 14.0948 18.9203 13.3773 18.4678 12.7936C18.0154 12.2099 17.3818 11.793 16.6667 11.6083M13.3334 1.60833C14.0504 1.79192 14.6859 2.20892 15.1397 2.79359C15.5936 3.37827 15.8399 4.09736 15.8399 4.8375C15.8399 5.57764 15.5936 6.29673 15.1397 6.88141C14.6859 7.46608 14.0504 7.88308 13.3334 8.06667M10.8334 4.83333C10.8334 6.67428 9.34099 8.16667 7.50004 8.16667C5.65909 8.16667 4.16671 6.67428 4.16671 4.83333C4.16671 2.99238 5.65909 1.5 7.50004 1.5C9.34099 1.5 10.8334 2.99238 10.8334 4.83333Z"
          stroke="#274AB1"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  };

  const tabs = SECTIONS;

  const handlePermissionChange = (tabId, roleId, permissionKey) => {
    setPermissions((prev) => {
      const nextValue = !prev[tabId]?.[roleId]?.[permissionKey];
      // Every codename now lives in exactly ONE section (pinned by a test), so
      // this loop writes a single entry today. It is kept because the save
      // collects checkbox state across ALL sections: the moment a codename is
      // listed twice again, a copy left stale here would silently re-add the
      // permission the admin just revoked — the defect this guard was built
      // for.
      const next = { ...prev };
      Object.keys(permissionCategories).forEach((catTabId) => {
        if (
          permissionCategories[catTabId].some((p) => p.key === permissionKey)
        ) {
          next[catTabId] = {
            ...next[catTabId],
            [roleId]: {
              ...next[catTabId]?.[roleId],
              [permissionKey]: nextValue,
            },
          };
        }
      });
      return next;
    });
  };

  // Helper function to detect if role permissions have changed
  const hasRoleChanged = (roleId) => {
    for (const tabId of Object.keys(permissionCategories)) {
      for (const permission of permissionCategories[tabId]) {
        const currentValue =
          permissions[tabId]?.[roleId]?.[permission.key] || false;
        const originalValue =
          originalPermissions[tabId]?.[roleId]?.[permission.key] || false;
        if (currentValue !== originalValue) {
          return true;
        }
      }
    }
    return false;
  };

  // Count roles with pending changes
  const pendingChangesCount = useMemo(() => {
    return roles.filter((role) => hasRoleChanged(role.id)).length;
  }, [roles, permissions, originalPermissions]);

  // Save permissions to API (only for changed roles)
  const handleSavePermissions = async () => {
    try {
      setSaving(true);
      setError(null);

      // Collect only changed roles for updates
      const changedRoleUpdates = [];

      for (const role of roles) {
        // API contract: assign_permissions answers 400 "This role cannot be
        // edited" for is_editable:false roles (e.g. CEO) — never send them.
        if (role.is_editable === false) {
          continue;
        }

        // Check if this role has any changes
        if (!hasRoleChanged(role.id)) {
          console.log(
            `No changes detected for role: ${role.name}, skipping API call`
          );
          continue;
        }

        console.log(
          `Changes detected for role: ${role.name}, preparing API call`
        );

        // Checked catalog permissions, merged with the role's codenames this
        // screen does not render — the backend replaces the full set, so
        // un-rendered codenames must survive the save.
        const rolePermissions = buildRolePermissionCodenames(
          role,
          permissionCategories,
          permissions
        );

        // Ids come only from availablePermissions, the GET
        // available_permissions/ response — which is exactly the set
        // assign_permissions accepts back since BE #293 (B10). Every id we send
        // therefore resolves by construction, so a legitimate save can never
        // hit the new 400 ("permission_ids contains n id(s) that are not club
        // permissions"). Codenames the role holds outside that set drop out
        // here and are carried through server-side instead.
        const permissionIds = availablePermissions
          .filter((perm) => rolePermissions.has(perm.codename))
          .map((perm) => perm.id);

        changedRoleUpdates.push({
          roleId: role.id,
          roleName: role.name,
          permissionIds,
        });
      }

      // If no changes, show message and return
      if (changedRoleUpdates.length === 0) {
        toast.success("No changes detected. All permissions are up to date!");
        return;
      }

      console.log(
        `Making ${changedRoleUpdates.length} API calls for changed roles:`,
        changedRoleUpdates.map((r) => r.roleName)
      );

      // Update permissions only for changed roles
      const updatePromises = changedRoleUpdates.map(
        ({ roleId, permissionIds }) =>
          rolesPermissionsService.updateRolePermissions(roleId, permissionIds)
      );

      const results = await Promise.allSettled(updatePromises);

      // Check if all updates were successful
      const failures = results.filter(
        (result) => result.status === "rejected" || !result.value?.success
      );

      if (failures.length === 0) {
        // Update the original permissions to match current state after successful save
        setOriginalPermissions(JSON.parse(JSON.stringify(permissions)));
        toast.success(
          `Permissions updated successfully for ${changedRoleUpdates.length} role(s)!`
        );
      } else {
        console.error("Some permission updates failed:", failures);
        toast.error("Some permissions could not be saved. Please try again.");
      }
    } catch (err) {
      console.error("Error saving permissions:", err);
      setError("Failed to save permissions");
      toast.error("Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  const closeCreateRoleModal = () => {
    if (creatingRole) return; // same "no-op while in flight" the save button gets from its own disabled state
    setShowCreateRoleModal(false);
    setNewRoleName("");
  };

  // POST /users/roles/ (rolesPermissionsService.createRole) — the endpoint
  // ClubRoleViewSet.perform_create pins to the caller's own club, so `name`
  // is the only field this screen ever sends. On success the role list is
  // reloaded through the same GET+rebuild pipeline the mount effect uses
  // (loadRolesAndPermissions), so the new row lands in every tab's
  // permission map exactly like every other role, editable immediately.
  const handleCreateRoleSubmit = async () => {
    const trimmedName = newRoleName.trim();
    if (!trimmedName) return; // no request for an empty name

    setCreatingRole(true);
    const result = await rolesPermissionsService.createRole(trimmedName);
    setCreatingRole(false);

    if (result.success) {
      setShowCreateRoleModal(false);
      setNewRoleName("");
      toast.success("Role created successfully!");
      await loadRolesAndPermissions();
      return;
    }

    if (result.status === 400) {
      // unique_together(name, club) on the backend — never surface its raw
      // serializer text (e.g. "The fields name, club must make a unique
      // set."), the only 400 this create form can realistically hit is a
      // duplicate name in this club.
      toast.error("A role with this name already exists.");
      return;
    }

    if (result.status === 403) {
      // The response interceptor (services/api.js handlePermissionError)
      // already toasts "Access denied. Required permission: edit_roles" for
      // every 403 in the app — nothing more to show here.
      return;
    }

    toast.error(result.error || "Failed to create role");
  };

  const renderCheckbox = (
    tabId,
    roleId,
    permissionKey,
    isChecked,
    isRowEditable = true
  ) => {
    const isDisabled = !canEditRoles || !isRowEditable;
    return (
      <div className="flex justify-center">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => handlePermissionChange(tabId, roleId, permissionKey)}
          disabled={isDisabled}
          className={`w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 ${
            isDisabled ? "opacity-50 cursor-not-allowed" : ""
          }`}
        />
      </div>
    );
  };

  const getTableClass = (columnCount) => {
    switch (columnCount) {
      case 2:
        return "w-full sm:table-fixed";
      case 3:
        return "w-full sm:table-fixed";
      case 4:
        return "w-full sm:table-fixed";
      case 5:
        return "w-full sm:table-fixed";
      case 6:
        return "w-full sm:table-fixed";
      default:
        return "w-full";
    }
  };

  const getColumnStyles = (columnCount, columnIndex) => {
    switch (columnCount) {
      case 2:
        return "w-1/2";
      case 3:
        return "w-1/3";
      case 4:
        return "w-1/4";
      case 5:
        return "w-1/5";
      case 6:
        return columnIndex === 0 ? "w-1/3" : "w-[13.333%]";
      default:
        return "";
    }
  };

  const renderTabContent = () => {
    const currentPermissions = permissions[activeTab] || {};

    switch (activeTab) {
      case "requests":
      case "pitches":
      case "contracts":
      case "squad_scouting":
      case "communication":
      case "team_permissions":
        const currentPermissionList = permissionCategories[activeTab] || [];
        const columnCount = currentPermissionList.length + 1; // +1 for role column

        // Mobile (<768px): one card per role, expanded rights as the same
        // checkboxes the table renders — state, handler and save path are
        // shared, only the presentation differs (LOOPUI Fix 2).
        if (isMobile) {
          return (
            <div className="bg-white rounded-lg">
              {roles.map((role) => {
                const isExpanded = expandedRoleIds.has(role.id);
                return (
                  <div
                    key={role.id}
                    className="border-b border-[#E8E7EA] last:border-b-0"
                  >
                    <button
                      type="button"
                      onClick={() => toggleRoleExpanded(role.id)}
                      aria-expanded={isExpanded}
                      className="w-full min-h-[44px] flex items-center justify-between gap-3 py-3 px-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="min-w-0 flex-1 font-medium text-[#414C5A] text-[16px] break-words">
                        {role.name}
                        {role.is_editable === false && (
                          <Tooltip
                            content={READ_ONLY_ROLE_HINT}
                            position="bottom-right"
                          >
                            <Lock
                              className="inline-block ml-1.5 w-3.5 h-3.5 text-[#8A94A6] align-[-2px]"
                              aria-label={READ_ONLY_ROLE_HINT}
                            />
                          </Tooltip>
                        )}
                      </span>
                      <ChevronDown
                        aria-hidden="true"
                        className={`w-4 h-4 text-[#8A94A6] shrink-0 transition-transform duration-200 ${
                          isExpanded ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-3">
                        {currentPermissionList.map((permission) => (
                          <div
                            key={permission.key}
                            className="flex items-center justify-between gap-3 min-h-[44px]"
                          >
                            <span className="text-[#303A47] text-[13px] font-medium whitespace-pre-line">
                              {permission.label}
                            </span>
                            {renderCheckbox(
                              activeTab,
                              role.id,
                              permission.key,
                              currentPermissions[role.id]?.[permission.key],
                              role.is_editable !== false
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        }

        return (
          <div>
            <div className="bg-white rounded-lg overflow-x-auto">
              <table className={`${getTableClass(columnCount)} table-sticky-first`}>
                <thead className="bg-white">
                  <tr>
                    <th
                      className={`${getColumnStyles(
                        columnCount,
                        0
                      )} text-left py-4 px-6 font-medium text-[#303A47] text-[14px]`}
                    >
                      ROLE
                    </th>
                    {currentPermissionList.map((permission, index) => (
                      <th
                        key={permission.key}
                        className={`${getColumnStyles(
                          columnCount,
                          index + 1
                        )} text-center py-4 px-6 font-medium text-[#303A47] text-[14px] whitespace-pre-line`}
                      >
                        {permission.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {roles.map((role) => (
                    <tr
                      key={role.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      {/* The role cell is the sticky first column below 640px
                          (.table-sticky-first, src/index.css) — it must stay
                          the FIRST cell of the row, so the read-only marker
                          lives inside it and never as a row of its own. */}
                      <td className="py-4 px-6 font-medium text-[#414C5A] text-[16px]">
                        {role.is_editable === false ? (
                          <span className="inline-flex items-center gap-1.5">
                            {role.name}
                            <Tooltip
                              content={READ_ONLY_ROLE_HINT}
                              position="bottom-right"
                            >
                              <Lock
                                className="w-3.5 h-3.5 text-[#8A94A6]"
                                aria-label={READ_ONLY_ROLE_HINT}
                              />
                            </Tooltip>
                          </span>
                        ) : (
                          role.name
                        )}
                      </td>
                      {currentPermissionList.map((permission) => (
                        <td key={permission.key} className="py-4 px-6">
                          {renderCheckbox(
                            activeTab,
                            role.id,
                            permission.key,
                            currentPermissions[role.id]?.[permission.key],
                            // API contract: is_editable:false rows (CEO) are
                            // read-only — assign_permissions would 400.
                            role.is_editable !== false
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="px-4 sm:px-12 py-4 border-b bg-white">
          <h1 className="text-xl font-bold text-gray-900">
            Roles & Permissions
          </h1>
        </div>
        <div className="px-4 sm:px-12 py-8 max-w-[1600px] mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-[#D6DBEA] p-8">
            <div className="flex items-center justify-center h-64">
              <div className="flex items-center gap-3">
                <svg
                  className="animate-spin h-6 w-6 text-blue-600"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className="text-gray-600">
                  Loading roles and permissions...
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      {/* Header */}
      <div className="px-4 sm:px-12 py-4 border-b bg-white">
        <h1 className="text-xl font-bold text-gray-900">Roles & Permissions</h1>
      </div>
      {/* max-w keeps 2560px centred (1440px stays untouched: 1344px content
          < 1600px cap). pb-28 below lg scrolls the save footer clear of the
          global support floating button (64px + 24px inset). It used to stop at
          md, and md IS 768 — so at exactly 768 the clearance was already gone
          while the button still sat on the Save button (QAF01-005, finding 16). */}
      <div className="px-4 sm:px-12 pt-8 pb-28 lg:pb-8 max-w-[1600px] mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-[#D6DBEA] overflow-hidden">
          {/* Tab Navigation */}
          <div className="border-b bg-white w-full">
            <div className="w-full relative">
              <nav
                ref={tabsNavRef}
                onScroll={updateTabsHint}
                className="flex overflow-x-auto thin-scrollbar w-full justify-between"
              >
                {tabs.map((tab) => {
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-4 py-3 whitespace-nowrap transition-all duration-200 mr-1 border-b-2 ${
                        activeTab === tab.id
                          ? "bg-[#348DFF] text-white font-medium text-base rounded-t-lg border-[#348DFF]"
                          : "text-[#1E1E1ECC] font-normal text-base hover:bg-gray-50 rounded-t-lg border-transparent"
                      }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
              {/* Visible "more tabs to the right" hint — only while the bar
                  can actually scroll further (no silent cut-off, LOOPUI
                  Fix 2). pointer-events-none keeps the tabs clickable. */}
              {tabsHaveMoreRight && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent"
                />
              )}
            </div>
          </div>

          {/* Tab Content */}
          <div className="">
            <div className="mb-6 px-6 bg-[#FAFBFC] py-4 border-b border-[#E8E7EA]">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 ">
                  {(() => {
                    const currentTab = tabs.find((t) => t.id === activeTab);
                    return (
                      <>
                        {customIcons[activeTab]}
                        <h2 className="text-[18px] font-bold text-[#1A212F]">
                          {currentTab?.label}
                        </h2>
                      </>
                    );
                  })()}
                </div>
                {/* Own-club custom role (QAV5). Same gate as every checkbox
                    in this grid — canEditRoles, no new permission check. */}
                {canEditRoles && (
                  <button
                    type="button"
                    onClick={() => setShowCreateRoleModal(true)}
                    className="min-h-[44px] inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 whitespace-nowrap"
                  >
                    <Plus className="w-4 h-4" />
                    Add Role
                  </button>
                )}
              </div>
            </div>

            {renderTabContent()}
          </div>

          {/* Footer with Save Button */}
          <div className="px-6 py-4 border-t bg-gray-50 flex justify-between items-center">
            {error && <div className="text-red-600 text-sm">{error}</div>}
            <div className="flex gap-2 ml-auto">
              <button
                onClick={handleSavePermissions}
                disabled={
                  saving ||
                  loading ||
                  pendingChangesCount === 0 ||
                  !canEditRoles
                }
                className={`px-6 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                  saving ||
                  loading ||
                  pendingChangesCount === 0 ||
                  !canEditRoles
                    ? "bg-gray-400 cursor-not-allowed text-white"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {saving && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                )}
                {saving
                  ? "Saving..."
                  : pendingChangesCount > 0
                  ? tr("Save Changes ({count} role{s})")
                      .replace("{count}", pendingChangesCount)
                      .replace("{s}", pendingChangesCount !== 1 ? "s" : "")
                  : "No Changes"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        variant="v2"
        isOpen={showCreateRoleModal}
        onClose={closeCreateRoleModal}
        onConfirm={handleCreateRoleSubmit}
        title="Add a new role"
        confirmText="Create Role"
        confirmButtonClass="bg-blue-600 hover:bg-blue-700"
        loading={creatingRole}
        confirmDisabled={!newRoleName.trim()}
      >
        <div className="mb-2 text-left">
          <label
            htmlFor="new-club-role-name"
            className="block text-[13px] font-medium text-[#374151] mb-1"
          >
            Role name
          </label>
          <input
            id="new-club-role-name"
            type="text"
            autoFocus
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleCreateRoleSubmit();
              } else if (e.key === "Escape") {
                closeCreateRoleModal();
              }
            }}
            disabled={creatingRole}
            maxLength={100}
            className="w-full min-h-[44px] px-3 py-2 border border-[#D6DBEA] rounded-lg text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>
      </ConfirmationModal>
    </div>
  );
};

export default RolesAndResponsibilities;
